import {
  Box,
  Button,
  Card,
  Collapsible,
  Input,
  NativeSelect,
  Text,
} from '@chakra-ui/react'
import { ChevronDown } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  Field,
  Modal,
  StateProvinceCombobox,
  Toggle,
} from '@mangovoice/mango-ui'

import { getPhoneNumberTypeSelectOptions } from '../constants'
import type { PhoneNumbersTranslateFn } from '../types/i18n.types'
import { text } from '../types/i18n.types'
import type {
  PhoneNumberListItem,
  PhoneNumberUpdatePayload,
  SelectOption,
} from '../types/phoneNumber.types'
import { formatPhoneNumber } from '../utils/formatPhoneNumber'
import { validateEditFields } from '../utils/validation'
import { ExtensionCombobox } from './ExtensionCombobox'
import { IntegrationCombobox } from './IntegrationCombobox'

const LOCAL_PRESENCE_OPTIONS = [
  { value: 0, label: 'Off' },
  { value: 1, label: 'Group 1' },
  { value: 2, label: 'Group 2' },
  { value: 3, label: 'Group 3' },
  { value: 4, label: 'Group 4' },
  { value: 5, label: 'Group 5' },
  { value: 6, label: 'Group 6' },
]

export interface EditPhoneNumberModalProps {
  phoneNumber: PhoneNumberListItem
  open: boolean
  onClose: () => void
  onSave: (payload: PhoneNumberUpdatePayload) => Promise<void>
  isSaving?: boolean
  t?: PhoneNumbersTranslateFn
  statuses?: SelectOption[]
  providers?: SelectOption[]
  extensions?: SelectOption[]
  faxMachines?: SelectOption[]
  integrations?: SelectOption[]
  isMangoInternal?: boolean
  // Deployment region (e.g. 'US' | 'GB'). Governs whether E911 requires a
  // state, mirroring the backend's get_default_region().
  defaultCountryCode?: string
}

export const EditPhoneNumberModal: React.FC<EditPhoneNumberModalProps> = ({
  phoneNumber,
  open,
  onClose,
  onSave,
  isSaving,
  t,
  statuses,
  providers,
  extensions,
  faxMachines,
  integrations,
  isMangoInternal,
  defaultCountryCode,
}) => {
  const [name, setName] = useState(phoneNumber.name || '')
  const [type, setType] = useState(phoneNumber.type || 'standard')
  const [statusId, setStatusId] = useState<string>(
    phoneNumber.status?.id?.toString() || '',
  )
  const [voiceProviderId, setVoiceProviderId] = useState<string>(
    phoneNumber.voice_provider_id?.toString() || '',
  )
  const [smsProviderId, setSmsProviderId] = useState<string>(
    phoneNumber.sms_provider_id?.toString() || '',
  )
  const [callerIdentification, setCallerIdentification] = useState(
    phoneNumber.caller_identification || '',
  )
  const [faxExtensionId, setFaxExtensionId] = useState<string>(
    phoneNumber.fax_extension?.id?.toString() || '',
  )
  const [extensionUuid, setExtensionUuid] = useState(
    phoneNumber.extension?.uuid || '',
  )
  const [sourceTracking, setSourceTracking] = useState(
    phoneNumber.prepend_string || '',
  )
  const [localPresence, setLocalPresence] = useState(
    phoneNumber.local_presence ?? 0,
  )

  const [smsEnabled, setSmsEnabled] = useState(!!phoneNumber.sms_activated_at)
  const [smsRelay, setSmsRelay] = useState(
    phoneNumber.sms_information?.relay ?? false,
  )
  const [integrationId, setIntegrationId] = useState<string>(
    phoneNumber.integration_id?.toString() || '',
  )
  const [campaignId, setCampaignId] = useState(phoneNumber.campaign_id || '')

  const [e911Address, setE911Address] = useState(
    phoneNumber.e911_information?.address || '',
  )
  const [e911Address2, setE911Address2] = useState(
    phoneNumber.e911_information?.address_2 || '',
  )
  const [e911City, setE911City] = useState(
    phoneNumber.e911_information?.city || '',
  )
  const [e911State, setE911State] = useState(
    phoneNumber.e911_information?.state || '',
  )
  const [e911PostalCode, setE911PostalCode] = useState(
    phoneNumber.e911_information?.postal_code || '',
  )

  // Section open/close state. `Collapsible` is uncontrolled inside a single
  // mount, but the modal stays mounted across open/close cycles, so we
  // manage the open flags ourselves. These hold the user override only —
  // `null` means "no override, fall through to the data-driven default"
  // (e.g. open E911 if it already has an address). Storing null instead
  // of a boolean avoids a flash of wrong open/closed state on first
  // render before the reset effect runs.
  const [userE911Open, setUserE911Open] = useState<boolean | null>(null)
  const [userAdvancedOpen, setUserAdvancedOpen] = useState<boolean | null>(null)
  const [userSmsOpen, setUserSmsOpen] = useState<boolean | null>(null)

  // Hold the latest phoneNumber in a ref so the reset effect below can read
  // fresh data without depending on the full object reference. Parents that
  // pass a new object literal with identical data on every render (common
  // with non-memoized React Query selectors) would otherwise wipe in-progress
  // edits every time they re-render.
  const phoneNumberRef = useRef(phoneNumber)
  phoneNumberRef.current = phoneNumber

  // Reset form state to the latest phoneNumber props whenever the modal
  // opens, or whenever the phoneNumber's identity (id) changes while open.
  // The modal stays mounted across close/reopen cycles, so without this,
  // unsaved edits leak between opens of the same number. Keying the effect
  // on `phoneNumber.id` (rather than the whole object) prevents same-data
  // re-renders from clobbering edits.
  useEffect(() => {
    if (!open) return
    const pn = phoneNumberRef.current
    setName(pn.name || '')
    setType(pn.type || 'standard')
    setStatusId(pn.status?.id?.toString() || '')
    setVoiceProviderId(pn.voice_provider_id?.toString() || '')
    setSmsProviderId(pn.sms_provider_id?.toString() || '')
    setCallerIdentification(pn.caller_identification || '')
    setFaxExtensionId(pn.fax_extension?.id?.toString() || '')
    setExtensionUuid(pn.extension?.uuid || '')
    setSourceTracking(pn.prepend_string || '')
    setLocalPresence(pn.local_presence ?? 0)
    setSmsEnabled(!!pn.sms_activated_at)
    setSmsRelay(pn.sms_information?.relay ?? false)
    setIntegrationId(pn.integration_id?.toString() || '')
    setCampaignId(pn.campaign_id || '')
    setE911Address(pn.e911_information?.address || '')
    setE911Address2(pn.e911_information?.address_2 || '')
    setE911City(pn.e911_information?.city || '')
    setE911State(pn.e911_information?.state || '')
    setE911PostalCode(pn.e911_information?.postal_code || '')

    // Clear any user override on open so the defaults below (which
    // depend on the latest phoneNumber props) take effect again.
    setUserE911Open(null)
    setUserAdvancedOpen(null)
    setUserSmsOpen(null)
  }, [phoneNumber.id, open])

  // Defaults for the section open state, derived from the phoneNumber:
  //   - E911 opens when there's already an address etc., so users see
  //     their data without an extra click.
  //   - SMS opens when SMS is activated, so the relay/integration/
  //     campaign-id settings are visible by default.
  //   - Advanced stays closed unless validation errors below force it
  //     open.
  const e911HasData =
    !!phoneNumber.e911_information?.address ||
    !!phoneNumber.e911_information?.address_2 ||
    !!phoneNumber.e911_information?.city ||
    !!phoneNumber.e911_information?.state ||
    !!phoneNumber.e911_information?.postal_code
  const smsHasData = !!phoneNumber.sms_activated_at

  // Always include the currently-assigned extension as an option so the
  // picker can render its number/name even when the fetched list is empty
  // or doesn't contain it (API failure, pagination, filter mismatch, etc.).
  // Without this we'd silently fall back to an Input that exposes the raw
  // UUID, which looks broken to end users.
  const mergedExtensions = useMemo<SelectOption[]>(() => {
    const list = extensions ? [...extensions] : []
    const currentUuid = phoneNumber.extension?.uuid
    if (!currentUuid) return list
    const alreadyPresent = list.some(
      (ext) => (ext.uuid ?? String(ext.id)) === currentUuid,
    )
    if (alreadyPresent) return list
    const currentNumber = phoneNumber.extension?.number ?? undefined
    // `id: 0` is a placeholder — the option's value is keyed off `uuid`,
    // and `extensionUuid` is what we round-trip on save, so the synthetic
    // id is never sent to the API.
    list.unshift({
      id: 0,
      uuid: currentUuid,
      number: currentNumber,
      name: currentNumber ?? currentUuid,
    })
    return list
  }, [extensions, phoneNumber.extension])

  const validationErrors = useMemo(
    () =>
      validateEditFields({
        name,
        callerIdentification,
        campaignId,
        sourceTracking,
        e911Address,
        e911Address2,
        e911City,
        e911State,
        e911PostalCode,
        e911Region: defaultCountryCode,
        // Coerce to strict boolean: the section is hidden when
        // `isMangoInternal` is falsy, so validation must track the same
        // gate. Passing `undefined` here would fall through to the
        // default (validate), re-creating the disabled-Save trap.
        validateE911: !!isMangoInternal,
        t,
      }),
    [
      name,
      callerIdentification,
      campaignId,
      sourceTracking,
      e911Address,
      e911Address2,
      e911City,
      e911State,
      e911PostalCode,
      defaultCountryCode,
      isMangoInternal,
      t,
    ],
  )

  const hasErrors = Object.keys(validationErrors).length > 0

  // Force-open a section whenever a validation error lives inside it so
  // users aren't staring at a disabled Save button with no visible reason.
  // The user-toggle state is consulted only when there's no error; on
  // first render it's `null`, which falls through to the data-driven
  // default so the section opens at the correct state without an
  // intermediate "false" flash.
  const isE911Open = !!validationErrors.e911 || (userE911Open ?? e911HasData)
  const isAdvancedOpen =
    !!validationErrors.callerIdentification || (userAdvancedOpen ?? false)
  const isSmsOpen = !!validationErrors.campaignId || (userSmsOpen ?? smsHasData)

  const isDirty = useMemo(() => {
    const pn = phoneNumber
    return (
      name !== (pn.name || '') ||
      type !== (pn.type || 'standard') ||
      statusId !== (pn.status?.id?.toString() || '') ||
      voiceProviderId !== (pn.voice_provider_id?.toString() || '') ||
      smsProviderId !== (pn.sms_provider_id?.toString() || '') ||
      callerIdentification !== (pn.caller_identification || '') ||
      faxExtensionId !== (pn.fax_extension?.id?.toString() || '') ||
      extensionUuid !== (pn.extension?.uuid || '') ||
      sourceTracking !== (pn.prepend_string || '') ||
      localPresence !== (pn.local_presence ?? 0) ||
      smsEnabled !== !!pn.sms_activated_at ||
      smsRelay !== (pn.sms_information?.relay ?? false) ||
      integrationId !== (pn.integration_id?.toString() || '') ||
      campaignId !== (pn.campaign_id || '') ||
      e911Address !== (pn.e911_information?.address || '') ||
      e911Address2 !== (pn.e911_information?.address_2 || '') ||
      e911City !== (pn.e911_information?.city || '') ||
      e911State !== (pn.e911_information?.state || '') ||
      e911PostalCode !== (pn.e911_information?.postal_code || '')
    )
  }, [
    phoneNumber,
    name,
    type,
    statusId,
    voiceProviderId,
    smsProviderId,
    callerIdentification,
    faxExtensionId,
    extensionUuid,
    sourceTracking,
    localPresence,
    smsEnabled,
    smsRelay,
    integrationId,
    campaignId,
    e911Address,
    e911Address2,
    e911City,
    e911State,
    e911PostalCode,
  ])

  const handleSave = useCallback(async () => {
    if (hasErrors) return
    const payload: PhoneNumberUpdatePayload = {
      name,
      type,
      status: statusId ? Number(statusId) : undefined,
      voice_provider: voiceProviderId ? Number(voiceProviderId) : undefined,
      sms_provider: smsProviderId ? Number(smsProviderId) : undefined,
      // Send '' (not undefined) when these fields are cleared so PATCH
      // actually persists the clear; undefined would be dropped from the
      // request body and the backend would treat the field as unchanged.
      caller_identification: callerIdentification,
      fax_extension: faxExtensionId ? Number(faxExtensionId) : null,
      extension_uuid: extensionUuid || null,
      prepend_string: sourceTracking,
      local_presence: localPresence,
      sms_enabled: smsEnabled,
      sms_relay: smsRelay,
      integration: integrationId ? Number(integrationId) : null,
      campaign_id: campaignId ? campaignId.toUpperCase() : '',
      // E911 is staff-only: the section isn't rendered for non-staff, so
      // their state is always the round-tripped initial values. Omitting
      // the keys (vs. sending seeded-but-unchanged values) keeps the PATCH
      // honest about what the user actually saw and could edit.
      ...(isMangoInternal && {
        e911_address: e911Address,
        e911_address_2: e911Address2,
        e911_city: e911City,
        e911_state: e911State,
        e911_postal_code: e911PostalCode,
      }),
    }
    try {
      await onSave(payload)
    } catch {
      // The mutation's `onError` already surfaces a toast describing the
      // backend validation failure. Swallow here so the rejection doesn't
      // bubble out of the click handler as an unhandled rejection (which
      // would otherwise alarm in Sentry on every routine 4xx).
    }
  }, [
    hasErrors,
    name,
    type,
    statusId,
    voiceProviderId,
    smsProviderId,
    callerIdentification,
    faxExtensionId,
    extensionUuid,
    sourceTracking,
    localPresence,
    smsEnabled,
    smsRelay,
    integrationId,
    campaignId,
    e911Address,
    e911Address2,
    e911City,
    e911State,
    e911PostalCode,
    isMangoInternal,
    onSave,
  ])

  const formattedNumber = formatPhoneNumber(phoneNumber.number)
  const dialogTitle = text(
    t,
    'phoneNumbers.edit.titleWithNumber',
    `Edit Phone Number — ${formattedNumber}`,
    { number: formattedNumber },
  )

  // Short summary shown in the closed E911 trigger header so users can
  // confirm the address at a glance without opening the section.
  const e911Summary = useMemo(() => {
    const parts = [e911Address, e911City, e911State].filter(Boolean)
    if (parts.length === 0) {
      return text(t, 'phoneNumbers.edit.e911NotConfigured', 'Not configured')
    }
    return parts.join(', ')
  }, [e911Address, e911City, e911State, t])

  return (
    <Modal
      open={open}
      onOpenChange={(e) => {
        if (!e.open) onClose()
      }}
      title={dialogTitle}
      maxWidth="xl"
      placement="top"
      footer={
        <>
          <Button
            variant="outline"
            borderRadius="lg"
            onClick={onClose}
            disabled={isSaving}
          >
            {text(t, 'phoneNumbers.edit.cancel', 'Cancel')}
          </Button>
          <Button
            colorPalette="brand"
            borderRadius="lg"
            onClick={handleSave}
            disabled={isSaving || hasErrors || !isDirty}
            loading={isSaving}
          >
            {text(t, 'phoneNumbers.edit.save', 'Save')}
          </Button>
        </>
      }
    >
      <Box display="flex" flexDirection="column" gap={4}>
        {/* ---------- General (always visible) ---------- */}
        <Field.Root required invalid={!!validationErrors.name}>
          <Field.Label>
            {text(t, 'phoneNumbers.edit.nameLabel', 'Name')}
          </Field.Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={text(
              t,
              'phoneNumbers.edit.namePlaceholder',
              'Phone number name',
            )}
          />
          {validationErrors.name && (
            <Field.ErrorText>{validationErrors.name}</Field.ErrorText>
          )}
        </Field.Root>

        {mergedExtensions.length > 0 ? (
          <Field.Root>
            <Field.Label>
              {text(t, 'phoneNumbers.edit.extensionLabel', 'Extension')}
            </Field.Label>
            <ExtensionCombobox
              t={t}
              extensions={mergedExtensions}
              value={extensionUuid}
              onChange={setExtensionUuid}
            />
            <Field.HelperText>
              {text(
                t,
                'phoneNumbers.edit.extensionHelp',
                'Optional extension for the phone number.',
              )}
            </Field.HelperText>
          </Field.Root>
        ) : (
          <Field.Root>
            <Field.Label>
              {text(t, 'phoneNumbers.edit.extensionLabel', 'Extension')}
            </Field.Label>
            <Input
              value={extensionUuid}
              onChange={(e) => setExtensionUuid(e.target.value)}
              placeholder={text(
                t,
                'phoneNumbers.edit.extensionPlaceholder',
                'Extension UUID',
              )}
            />
          </Field.Root>
        )}

        {faxMachines && faxMachines.length > 0 && (
          <Field.Root>
            <Field.Label>
              {text(
                t,
                'phoneNumbers.edit.sendFaxesFromLabel',
                'Send Faxes From',
              )}
            </Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                value={faxExtensionId}
                onChange={(e) => setFaxExtensionId(e.target.value)}
              >
                <option value="">
                  {text(
                    t,
                    'phoneNumbers.edit.usePbxDefault',
                    'Use PBX Default',
                  )}
                </option>
                {faxMachines.map((fm) => (
                  <option key={fm.id} value={fm.id}>
                    {fm.name}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>
        )}

        {/* Source Tracking + Local Presence — both narrow optional
                    fields, paired to save vertical space. */}
        <Box display="flex" gap={3}>
          <Field.Root flex={1} invalid={!!validationErrors.sourceTracking}>
            <Field.Label>
              {text(
                t,
                'phoneNumbers.edit.sourceTrackingLabel',
                'Source Tracking',
              )}
            </Field.Label>
            <Input
              value={sourceTracking}
              onChange={(e) => setSourceTracking(e.target.value)}
              maxLength={6}
            />
            {validationErrors.sourceTracking ? (
              <Field.ErrorText>
                {validationErrors.sourceTracking}
              </Field.ErrorText>
            ) : (
              <Field.HelperText>
                {text(
                  t,
                  'phoneNumbers.edit.sourceTrackingHelp',
                  'Prepend up to 6 characters to the Caller ID for inbound calls. This is commonly used to track ad campaigns.',
                )}
              </Field.HelperText>
            )}
          </Field.Root>

          <Field.Root flex={1}>
            <Field.Label>
              {text(
                t,
                'phoneNumbers.edit.localPresenceLabel',
                'Local Presence',
              )}
            </Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                value={localPresence}
                onChange={(e) => setLocalPresence(Number(e.target.value))}
              >
                {LOCAL_PRESENCE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
            <Field.HelperText>
              {text(
                t,
                'phoneNumbers.edit.localPresenceHelp',
                'Local presence groups create a pool of numbers that a local-presence-enabled line extension can place a call from in order to appear local to the callee.',
              )}
            </Field.HelperText>
          </Field.Root>
        </Box>

        {/* ---------- E911 (mango-internal, collapsible) ---------- */}
        {isMangoInternal && (
          <CollapsibleSection
            title={text(
              t,
              'phoneNumbers.edit.e911Section',
              'Emergency 911 (E911) Settings',
            )}
            summary={e911Summary}
            open={isE911Open}
            onOpenChange={setUserE911Open}
          >
            {validationErrors.e911 && (
              <Text fontSize="xs" color="fg.error">
                {validationErrors.e911}
              </Text>
            )}

            <Field.Root>
              <Field.Label>
                {text(t, 'phoneNumbers.edit.e911AddressLabel', 'Address')}
              </Field.Label>
              <Input
                value={e911Address}
                onChange={(e) => setE911Address(e.target.value)}
              />
            </Field.Root>

            <Field.Root>
              <Field.Label>
                {text(
                  t,
                  'phoneNumbers.edit.e911Address2Label',
                  'Address apartment/suite',
                )}
              </Field.Label>
              <Input
                value={e911Address2}
                onChange={(e) => setE911Address2(e.target.value)}
              />
            </Field.Root>

            <Box display="flex" gap={3}>
              <Field.Root flex={2}>
                <Field.Label>
                  {text(t, 'phoneNumbers.edit.e911CityLabel', 'City')}
                </Field.Label>
                <Input
                  value={e911City}
                  onChange={(e) => setE911City(e.target.value)}
                />
              </Field.Root>
              <Field.Root flex={1} minW={0}>
                <Field.Label htmlFor="phone-numbers-edit-e911-state">
                  {text(
                    t,
                    'phoneNumbers.edit.e911StateLabel',
                    'State/Province',
                  )}
                </Field.Label>
                <StateProvinceCombobox
                  variant="phoneNumber"
                  value={e911State}
                  onValueChange={setE911State}
                  allowEmpty
                  resetSearchKey={open}
                  id="phone-numbers-edit-e911-state"
                  placeholder={text(
                    t,
                    'phoneNumbers.create.searchStateProvince',
                    'Search...',
                  )}
                  emptySearchMessage={text(
                    t,
                    'phoneNumbers.create.noStateProvinceMatches',
                    'No matches',
                  )}
                />
              </Field.Root>
              <Field.Root flex={1}>
                <Field.Label>
                  {text(
                    t,
                    'phoneNumbers.edit.e911PostalCodeLabel',
                    'Postal Code',
                  )}
                </Field.Label>
                <Input
                  value={e911PostalCode}
                  onChange={(e) => setE911PostalCode(e.target.value)}
                />
              </Field.Root>
            </Box>

            <Text fontSize="sm" color="fg.muted">
              {text(
                t,
                'phoneNumbers.edit.e911Disclaimer',
                "E911 is a system used in North America to automatically provide the caller's location to 911 dispatchers. Please make sure this information is accurate--someone's life could depend on it.",
              )}
            </Text>
          </CollapsibleSection>
        )}

        {/* ---------- Advanced (mango-internal, collapsible) ---------- */}
        {isMangoInternal && (
          <CollapsibleSection
            title={text(t, 'phoneNumbers.edit.advancedSection', 'Advanced')}
            required
            open={isAdvancedOpen}
            onOpenChange={setUserAdvancedOpen}
          >
            {/* Type + Status — both small dropdowns, paired. */}
            <Box display="flex" gap={3}>
              <Field.Root flex={1} required>
                <Field.Label>
                  {text(t, 'phoneNumbers.edit.typeLabel', 'Type')}
                </Field.Label>
                <NativeSelect.Root>
                  <NativeSelect.Field
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                  >
                    {getPhoneNumberTypeSelectOptions(t).map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>

              {statuses && statuses.length > 0 && (
                <Field.Root flex={1} required>
                  <Field.Label>
                    {text(t, 'phoneNumbers.edit.statusLabel', 'Status')}
                  </Field.Label>
                  <NativeSelect.Root>
                    <NativeSelect.Field
                      value={statusId}
                      onChange={(e) => setStatusId(e.target.value)}
                    >
                      <option value="">
                        {text(
                          t,
                          'phoneNumbers.edit.selectStatus',
                          '— Select Status —',
                        )}
                      </option>
                      {statuses.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>
              )}
            </Box>

            {/* Voice Provider + SMS Provider — paired. Skip the
                        whole pair if no providers list (rare but matches
                        the original conditional). */}
            {providers && providers.length > 0 && (
              <Box display="flex" gap={3}>
                <Field.Root flex={1} required>
                  <Field.Label>
                    {text(
                      t,
                      'phoneNumbers.edit.voiceProviderLabel',
                      'Voice Provider',
                    )}
                  </Field.Label>
                  <NativeSelect.Root>
                    <NativeSelect.Field
                      value={voiceProviderId}
                      onChange={(e) => setVoiceProviderId(e.target.value)}
                    >
                      <option value="">
                        {text(
                          t,
                          'phoneNumbers.edit.selectProvider',
                          '— Select —',
                        )}
                      </option>
                      {providers.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>

                <Field.Root flex={1}>
                  <Field.Label>
                    {text(
                      t,
                      'phoneNumbers.edit.smsProviderLabel',
                      'SMS Provider',
                    )}
                  </Field.Label>
                  <NativeSelect.Root>
                    <NativeSelect.Field
                      value={smsProviderId}
                      onChange={(e) => setSmsProviderId(e.target.value)}
                    >
                      {providers.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </NativeSelect.Field>
                    <NativeSelect.Indicator />
                  </NativeSelect.Root>
                </Field.Root>
              </Box>
            )}

            <Field.Root invalid={!!validationErrors.callerIdentification}>
              <Field.Label>
                {text(t, 'phoneNumbers.edit.callerIdLabel', 'Caller ID')}
              </Field.Label>
              <Input
                value={callerIdentification}
                onChange={(e) => setCallerIdentification(e.target.value)}
                maxLength={15}
              />
              {validationErrors.callerIdentification && (
                <Field.ErrorText>
                  {validationErrors.callerIdentification}
                </Field.ErrorText>
              )}
            </Field.Root>
          </CollapsibleSection>
        )}

        {/* ---------- SMS (mango-internal, collapsible) ---------- */}
        {isMangoInternal && (
          <CollapsibleSection
            title={text(t, 'phoneNumbers.edit.smsSection', 'SMS Settings')}
            open={isSmsOpen}
            onOpenChange={setUserSmsOpen}
          >
            <Box display="flex" flexDirection="column" gap={2}>
              <Toggle
                size="sm"
                colorPalette="brand"
                checked={smsEnabled}
                onCheckedChange={(e) => {
                  setSmsEnabled(e.checked)
                  if (!e.checked) {
                    setSmsRelay(false)
                    setIntegrationId('')
                  }
                }}
                display="flex"
              >
                {text(t, 'phoneNumbers.edit.smsEnabledLabel', 'SMS Enabled')}
              </Toggle>

              <Toggle
                size="sm"
                colorPalette="brand"
                checked={smsRelay}
                onCheckedChange={(e) => {
                  setSmsRelay(e.checked)
                  if (!e.checked) {
                    setIntegrationId('')
                  }
                }}
                disabled={!smsEnabled}
                display="flex"
              >
                {text(t, 'phoneNumbers.edit.smsRelayLabel', 'SMS Relay')}
              </Toggle>
            </Box>

            {smsEnabled && (
              <Box
                bg="bg.error"
                borderWidth="1px"
                borderColor="error.muted"
                borderRadius="md"
                p={3}
              >
                <Text fontSize="sm" color="red.fg">
                  {text(
                    t,
                    'phoneNumbers.edit.smsEnabledWarning',
                    'Warning: enabling SMS will trigger a port-in request of the eSPID on this number, which may cause SMS downtime for the customer. Please make sure this is the desired action before proceeding.',
                  )}
                </Text>
              </Box>
            )}

            {/* SMS Relay Integration + Campaign ID — paired 2:1.
                        Integration's combobox needs more width than the
                        7-char Campaign ID input. Campaign ID's long
                        helper text wraps below its own input within its
                        Field.Root, so the rows can have unequal heights
                        without affecting the pair. */}
            <Box display="flex" gap={3} alignItems="flex-start">
              <Field.Root flex={2}>
                <Field.Label>
                  {text(
                    t,
                    'phoneNumbers.edit.smsRelayIntegrationLabel',
                    'SMS Relay Integration',
                  )}
                </Field.Label>
                <IntegrationCombobox
                  t={t}
                  integrations={integrations ?? []}
                  value={integrationId}
                  onChange={setIntegrationId}
                  disabled={!smsRelay}
                />
                {!smsRelay && (
                  <Field.HelperText>
                    {text(
                      t,
                      'phoneNumbers.edit.smsRelayIntegrationDisabledHelp',
                      'Enable SMS Relay to choose an integration.',
                    )}
                  </Field.HelperText>
                )}
              </Field.Root>

              <Field.Root flex={1} invalid={!!validationErrors.campaignId}>
                <Field.Label>
                  {text(t, 'phoneNumbers.edit.campaignIdLabel', 'Campaign ID')}
                </Field.Label>
                <Input
                  value={campaignId}
                  onChange={(e) => setCampaignId(e.target.value.toUpperCase())}
                  maxLength={7}
                />
                {validationErrors.campaignId ? (
                  <Field.ErrorText>
                    {validationErrors.campaignId}
                  </Field.ErrorText>
                ) : (
                  <Field.HelperText>
                    {text(
                      t,
                      'phoneNumbers.edit.campaignIdHelp',
                      'Required for sending SMS. Prior to setting this value, ensure that a campaign ID is linked to this number in the respective provider. Failure to do so may cause disruption of service, delivery failures, and/or fines.',
                    )}
                  </Field.HelperText>
                )}
              </Field.Root>
            </Box>
          </CollapsibleSection>
        )}
      </Box>
    </Modal>
  )
}

interface CollapsibleSectionProps {
  title: string
  summary?: string
  /**
   * When true, renders a red `*` after the section title so users know
   * the section contains save-blocking required fields they'd otherwise
   * miss while it's collapsed. Matches the convention used by
   * `PbxSettingsAdvancedSection` and Chakra's `<Field.Root required>`
   * label asterisk, so users hit the same symbol at section and field
   * levels.
   */
  required?: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  children: React.ReactNode
}

/**
 * Card-styled `Collapsible` wrapper used for the optional E911/Advanced/SMS
 * groups. Visually consistent with `[libraries/pbx-settings-ui/src/components/PbxSettingsAdvancedSection.tsx]`
 * but local to the modal: the title is plain text from the caller and the
 * trigger renders an optional one-line summary on the right (e.g. an E911
 * address preview) so users can confirm what's inside without expanding.
 */
const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  summary,
  required,
  open,
  onOpenChange,
  children,
}) => (
  <Collapsible.Root open={open} onOpenChange={(d) => onOpenChange(d.open)}>
    <Card.Root bg="bg.panel" rounded="xl">
      <Card.Header p={2}>
        <Collapsible.Trigger asChild>
          <Button
            variant="ghost"
            justifyContent="space-between"
            width="full"
            fontWeight="700"
          >
            <Box as="span" textAlign="left" flex="1" minW={0}>
              {title}
              {required && (
                <Text as="span" color="fg.error" ml={1} aria-hidden>
                  *
                </Text>
              )}
            </Box>
            {summary && (
              <Text
                fontSize="sm"
                fontWeight="400"
                color="fg.muted"
                truncate
                maxW="50%"
                mr={2}
              >
                {summary}
              </Text>
            )}
            <Collapsible.Indicator
              transition="transform 0.2s"
              _open={{ transform: 'rotate(-180deg)' }}
            >
              <ChevronDown size={16} />
            </Collapsible.Indicator>
          </Button>
        </Collapsible.Trigger>
      </Card.Header>
      <Collapsible.Content>
        <Card.Body p={6} pt={2}>
          <Box display="flex" flexDirection="column" gap={4}>
            {children}
          </Box>
        </Card.Body>
      </Collapsible.Content>
    </Card.Root>
  </Collapsible.Root>
)
